import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Medal, Trophy, ChartBar, Star, Scales, Flag, ClockCounterClockwise, X, ArrowSquareOut } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

const RARITY = {
  Common: "border-slate-500/30 text-slate-300", Distinguished: "border-cyan-600/30 text-cyan-300",
  Rare: "border-ff-gold/40 text-ff-gold", Legendary: "border-purple-500/40 text-purple-300",
  Singular: "border-ff-brass text-ff-brass",
};
const RARITY_ORDER = ["All", "Singular", "Legendary", "Rare", "Distinguished", "Common"];

export default function Prestige() {
  const [p, setP] = useState(null);
  const [titles, setTitles] = useState(null);
  const [records, setRecords] = useState({});
  const [dispatches, setDispatches] = useState([]);
  const [firsts, setFirsts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [history, setHistory] = useState(null); // {title, entries}
  const [rivals, setRivals] = useState([]);
  const [compare, setCompare] = useState(null);

  const loadTitles = useCallback(() => api.get("/prestige/titles").then((r) => setTitles(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/prestige").then((r) => setP(r.data)).catch(() => {});
    loadTitles();
    api.get("/prestige/records").then((r) => setRecords(r.data)).catch(() => {});
    api.get("/hall-dispatches").then((r) => setDispatches(r.data.dispatches)).catch(() => {});
    api.get("/prestige/historic-firsts").then((r) => setFirsts(r.data.firsts)).catch(() => {});
    api.get("/rivals").then((r) => setRivals(r.data.rivals || [])).catch(() => {});
  }, [loadTitles]);

  const setPrimary = async (tid) => {
    try { await api.post("/prestige/titles/primary", { title_id: tid }); toast.success("Primary title set"); loadTitles(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openHistory = async (t) => {
    try { const { data } = await api.get(`/prestige/singular/${t.title_id}`); setHistory({ title: t, entries: data.history || [] }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openCompare = async (id) => {
    try { const { data } = await api.get(`/rivals/compare/${id}`); setCompare(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!p) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted" data-testid="prestige-loading">Loading prestige…</div>;
  const maxSrc = Math.max(1, ...Object.values(p.breakdown || {}));
  const allTitles = titles?.titles || [];
  const shown = filter === "All" ? allTitles : allTitles.filter((t) => t.rarity === filter);

  return (
    <div className="space-y-6" data-testid="prestige-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Crown size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Prestige & Renown</h1>
        </div>
        <Link to="/hall-of-renown" data-testid="prestige-hall-link" className="inline-flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-brass transition-colors hover:border-ff-gold hover:text-ff-gold">
          <Trophy size={16} weight="duotone" /> Hall of Renown <ArrowSquareOut size={13} />
        </Link>
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="prestige-summary">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-ff-brass">Prestige Tier</div>
            <div className="font-display text-2xl font-bold text-ff-gold" data-testid="prestige-tier">{p.tier}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-ff-brass">Total Prestige</div>
            <div className="font-display text-3xl font-bold text-ff-parchment" data-testid="prestige-total">{p.total}</div>
          </div>
          {p.percentile != null && <div className="text-right"><div className="text-xs uppercase tracking-widest text-ff-brass">Standing</div><div className="text-lg font-semibold text-ff-parchment">Top {p.percentile}%</div></div>}
        </div>
        {!p.eligible && <p className="mt-2 text-xs text-ff-muted">This account is not eligible for competitive Prestige.</p>}
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="prestige-breakdown">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><ChartBar size={20} weight="duotone" /> Source Breakdown</h2>
        <div className="mt-3 space-y-2">
          {Object.entries(p.breakdown || {}).length === 0 && <p className="text-sm text-ff-muted">Complete ranked expeditions to build Prestige.</p>}
          {Object.entries(p.breakdown || {}).map(([k, v]) => (
            <div key={k} className="text-sm">
              <div className="flex justify-between text-ff-muted"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="text-ff-parchment">{v}</span></div>
              <div className="mt-0.5 h-2 w-full overflow-hidden rounded-full bg-ff-bg"><div className="h-full bg-ff-brass" style={{ width: `${(v / maxSrc) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="prestige-titles">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Medal size={20} weight="duotone" /> Titles ({allTitles.length})</h2>
          <div className="flex flex-wrap gap-1" data-testid="title-rarity-filter">
            {RARITY_ORDER.map((r) => (
              <button key={r} onClick={() => setFilter(r)} data-testid={`filter-${r.toLowerCase()}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors ${filter === r ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>{r}</button>
            ))}
          </div>
        </div>
        <p className="mt-1 text-xs text-ff-muted">Select a title to fly it as your primary. Singular titles show their ownership history.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((t) => (
            <div key={t.title_id} data-testid={`title-${t.title_id}`}
              className={`rounded-md border p-3 transition-colors ${titles.primary_title_id === t.title_id ? "bg-ff-gold/10" : ""} ${RARITY[t.rarity] || "border-ff-brass/25"}`}>
              <div className="flex items-center gap-1.5 font-semibold text-ff-parchment">{t.rarity === "Singular" && <Star size={14} weight="fill" className="text-ff-brass" />}{t.name}</div>
              <div className="text-[11px] uppercase tracking-wider">{t.rarity}{titles.primary_title_id === t.title_id ? " · Primary" : ""}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setPrimary(t.title_id)} data-testid={`title-primary-${t.title_id}`} className="rounded-sm border border-ff-brass/30 px-2 py-1 text-[11px] text-ff-parchment hover:border-ff-gold hover:text-ff-gold">Set Primary</button>
                {t.rarity === "Singular" && <button onClick={() => openHistory(t)} data-testid={`title-history-${t.title_id}`} className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-2 py-1 text-[11px] text-ff-brass hover:border-ff-gold"><ClockCounterClockwise size={12} /> History</button>}
              </div>
            </div>
          ))}
          {shown.length === 0 && <p className="text-sm text-ff-muted">No titles in this category yet — earn them through verified accomplishments.</p>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="ff-panel rounded-md p-5" data-testid="prestige-records">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Trophy size={20} weight="duotone" /> Records & Distinctions</h2>
          <div className="mt-3 space-y-1.5 text-sm">
            {Object.entries(records).length === 0 && <p className="text-ff-muted">No personal records yet.</p>}
            {Object.entries(records).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="capitalize text-ff-muted">{k.replace(/_/g, " ")}</span><span className="text-ff-parchment">{String(v)}</span></div>
            ))}
          </div>
        </div>
        <div className="ff-panel rounded-md p-5" data-testid="prestige-firsts">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Flag size={20} weight="duotone" /> Historic Firsts</h2>
          <div className="mt-3 space-y-2">
            {firsts.length === 0 && <p className="text-sm text-ff-muted">No Historic Firsts recorded yet.</p>}
            {firsts.slice(0, 8).map((f) => (
              <div key={f.key} className="border-b border-ff-brass/10 pb-1.5 text-sm last:border-0" data-testid={`first-${f.key}`}>
                <div className="text-ff-parchment">{f.description}</div>
                <div className="text-[11px] text-ff-brass">{f.holder}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="prestige-rival-compare">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Scales size={20} weight="duotone" /> Rival Prestige</h2>
        {rivals.length === 0 ? (
          <p className="mt-2 text-sm text-ff-muted">Follow rivals on the <Link to="/rivals" className="text-ff-brass hover:text-ff-gold">Rivals page</Link> to compare Prestige.</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {rivals.map((r) => (
              <button key={r.user_id} onClick={() => openCompare(r.user_id)} data-testid={`prestige-compare-${r.user_id}`}
                className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold">
                Compare vs {r.name}
              </button>
            ))}
          </div>
        )}
        {compare && compare.prestige && (
          <div className="mt-4 rounded-md border border-ff-brass/20 bg-ff-bg/50 p-3" data-testid="prestige-compare-result">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ff-muted">You{compare.prestige.you_tier ? ` · ${compare.prestige.you_tier}` : ""}</span>
              <span className="text-ff-muted">{compare.rival.name}{compare.prestige.rival_tier ? ` · ${compare.prestige.rival_tier}` : ""}</span>
            </div>
            <div className="mt-1 flex items-center justify-between font-display text-2xl font-bold">
              <span className={compare.prestige.you >= compare.prestige.rival ? "text-ff-gold" : "text-ff-parchment"}>{compare.prestige.you}</span>
              <span className="text-xs uppercase tracking-widest text-ff-brass">Prestige</span>
              <span className={compare.prestige.rival > compare.prestige.you ? "text-ff-gold" : "text-ff-parchment"}>{compare.prestige.rival}</span>
            </div>
            <p className="mt-2 text-xs text-ff-brass">{compare.momentum}</p>
          </div>
        )}
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="prestige-dispatches">
        <h2 className="text-lg font-semibold text-ff-gold">Hall Dispatches</h2>
        <div className="mt-3 space-y-2">
          {dispatches.length === 0 && <p className="text-sm text-ff-muted">No dispatches yet.</p>}
          {dispatches.slice(0, 12).map((d, i) => <p key={i} className="text-sm text-ff-parchment/90">◆ {d.text}</p>)}
        </div>
      </div>

      {history && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setHistory(null)} data-testid="singular-history-modal">
          <div className="ff-panel max-h-[85vh] w-full max-w-md overflow-y-auto rounded-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 font-display text-xl font-semibold text-ff-brass"><Star size={18} weight="fill" /> {history.title.name}</h3>
              <button onClick={() => setHistory(null)} className="text-ff-muted hover:text-ff-parchment"><X size={20} /></button>
            </div>
            <p className="mb-3 text-xs text-ff-muted">Singular title — held by one explorer at a time. Full ownership lineage below.</p>
            <div className="space-y-2">
              {history.entries.length === 0 && <p className="text-sm text-ff-muted">No recorded holders yet.</p>}
              {history.entries.map((h, i) => (
                <div key={i} className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-2.5 text-sm" data-testid={`singular-entry-${i}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold text-ff-parchment">{h.holder}</span><span className="text-[11px] uppercase tracking-wider text-ff-brass">{h.reason}</span></div>
                  <div className="text-[11px] text-ff-muted">Acquired {String(h.acquired_at).slice(0, 10)}{h.lost_at ? ` · Lost ${String(h.lost_at).slice(0, 10)}` : " · Current holder"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
